import { z } from 'zod';

/**
 * Prijenos datoteke ide u dva koraka: ruta izdaje potpisani URL, klijent šalje
 * bajtove direktno u R2 kroz `/v1/files` (Worker ne prolazi kroz binarni sadržaj).
 *
 * Oblik je zajednički za sve rute koje izdaju dozvolu za prijenos
 * (`/v1/progress/photos/upload-url`, `/v1/nutrition/meals/:id/photo-url`) —
 * `apps/api/src/lib/storage.ts` ga gradi na jednom mjestu, pa i ugovor ima
 * jednu shemu umjesto kopije po domeni.
 */
export const uploadRequestInput = z.object({
  /** MIME tip datoteke; server prihvaća jpeg, png, webp i heic. */
  contentType: z.string().min(1),
  bytes: z.number().int().positive(),
});

export const uploadGrantOutput = z.object({
  /** Ključ u bucketu. Klijent ga vraća pri potvrdi zapisa. */
  key: z.string(),
  /** Apsolutni URL prema `/v1/files`; potpis vrijedi za točno jedan `PUT`. */
  uploadUrl: z.string(),
  expiresAt: z.string(),
  maxBytes: z.number().int().positive(),
});

/**
 * Ograničenja koja server primjenjuje i pri izdavanju dozvole i pri samom upisu
 * (`assertImage` u `apps/api/src/lib/storage.ts`).
 *
 * Ovdje stoje da ih sučelje može provjeriti PRIJE slanja i odmah reći što nije u
 * redu, umjesto da korisnik čeka prijenos od 20 MB pa dobije 413. Mjerodavan
 * ostaje server: svaka dozvola nosi vlastiti `maxBytes`, a `/v1/files` uz to
 * njuška stvarne bajtove — deklarirani tip je samo tvrdnja klijenta.
 */
export const imageContentTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;
export const maxImageBytes = 12 * 1024 * 1024;

/** `IMAGE/JPEG; charset=x` i `image/jpeg` su isti tip — usporedba ide nad normaliziranim oblikom. */
export function isAllowedImageType(contentType: string | null | undefined): boolean {
  const normalized = (contentType ?? '').split(';')[0]!.trim().toLowerCase();
  return (imageContentTypes as readonly string[]).includes(normalized);
}

export type ImageContentType = (typeof imageContentTypes)[number];
export type UploadRequestInput = z.infer<typeof uploadRequestInput>;
export type UploadGrantOutput = z.infer<typeof uploadGrantOutput>;
